class GenerateDiamondImage extends HTMLElement {
  constructor() {
    super();
    this.button = null;
    this.generating = false;
  }

  connectedCallback() {
    this.button = this.querySelector("[data-generate-image]");
    if (!this.button) return;
    this.originalButtonHTML = this.button.innerHTML;
    this.button.addEventListener("click", () => this.generate());

    if (window.innerWidth >= 750) {
      const submitRow = document.querySelector(".quantity-submit-row");
      if (submitRow && this.parentElement !== submitRow.parentElement) {
        submitRow.before(this);
      }
    }
    this.style.display = 'block';

    // Listen for variant changes — the theme triggers 'change' on .original-selector
    // when the customer picks a different metal/option.
    // Use a broad selector since the wrapper class differs across page templates.
    const variantSelect = document.querySelector(".original-selector");
    if (variantSelect) {
      variantSelect.addEventListener("change", () => this.onVariantChange(variantSelect.value));
    }

    // Auto-display cached image from session storage after gallery has rendered
    setTimeout(() => this.loadFromSession(), 1200);
  }

  onVariantChange(variantId) {
    if (!variantId) return;

    // Read SKU and image directly from the selected <option> data attributes
    // (set in complete-engagement-ring.liquid on each variant option)
    const variantSelect = document.querySelector(".original-selector");
    const selectedOption = variantSelect?.querySelector(`option[value="${variantId}"]`);

    const sku = selectedOption?.dataset.sku;
    const imageUrl = selectedOption?.dataset.image;

    if (sku) this.dataset.ringSku = sku;
    if (imageUrl) this.dataset.ringImageUrl = imageUrl;

    // Clear the Liquid-rendered cached URL — it belongs to the previous variant
    delete this.dataset.cachedImageUrl;

    // Remove any existing AI-generated slide and thumbnail
    const slidesContainer = document.querySelector(".MagicToolboxSlides");
    const selectorsContainer = document.querySelector(".MagicToolboxSelectorsContainer");
    slidesContainer?.querySelector('[data-slide-id="ai-generated-diamond"]')?.remove();
    selectorsContainer?.querySelector('[data-slide-id="ai-generated-diamond"]')?.remove();

    // Reset the button so the customer can generate for the new variant
    this.style.display = "block";
    this.generating = false;
    this.button.disabled = false;
    this.button.innerHTML = this.originalButtonHTML;

    // Check if we already have a cached image for this variant
    this.loadFromSession();
  }

  // Read the SKU of the currently selected variant directly from the DOM at call time.
  // This is more reliable than data-ring-sku which may not update if the variant
  // change event fired on a different select than the one we're listening to.
  currentRingSku() {
    const select = document.querySelector(".original-selector");
    if (select?.value) {
      const option = select.querySelector(`option[value="${select.value}"]`);
      if (option?.dataset.sku) return option.dataset.sku;
    }
    // Fall back to the data attribute set at page load via Liquid
    return this.dataset.ringSku || "";
  }

  currentRingImageUrl() {
    const select = document.querySelector(".original-selector");
    if (select?.value) {
      const option = select.querySelector(`option[value="${select.value}"]`);
      if (option?.dataset.image) return option.dataset.image;
    }
    return this.dataset.ringImageUrl || "";
  }

  sessionKey() {
    const sku = this.currentRingSku();
    const cert = this.dataset.diamondCertNumber || "";
    return `diamond-swap:${sku}:${cert}`;
  }

  loadFromSession() {
    // First check: Liquid-rendered Shopify Files URL (persists across sessions)
    const cachedUrl = this.dataset.cachedImageUrl;
    if (cachedUrl) {
      this.insertIntoGallery(cachedUrl);
      this.saveToSession(cachedUrl);
      this.button.innerHTML = "Image Generated";
      this.style.display = "none";
      return;
    }

    // Second check: sessionStorage (current session only)
    const key = this.sessionKey();
    if (!key || key === "diamond-swap::") return;
    try {
      const cached = sessionStorage.getItem(key);
      if (cached) {
        this.insertIntoGallery(cached);
        this.button.innerHTML = "Image Generated";
        this.style.display = "none";
      }
    } catch (e) {
      // sessionStorage unavailable (private browsing restrictions etc.)
    }
  }

  saveToSession(imageUrl) {
    const key = this.sessionKey();
    if (!key || key === "diamond-swap::") return;
    try {
      // Only cache Shopify CDN URLs — skip large base64 data URIs to stay within quota
      if (!imageUrl.startsWith("data:")) {
        sessionStorage.setItem(key, imageUrl);
      }
    } catch (e) {
      // Quota exceeded or unavailable — silently skip
    }
  }

  async generate() {
    if (this.generating) return;
    this.generating = true;
    this.button.disabled = true;
    this.button.style.display = 'none';

    
    let resolveReady;
    const readyPromise = new Promise((r) => { resolveReady = r; });
    const loaderPromise = this.runColouredLoader(readyPromise);

    try {
      // Always read ring image and SKU fresh from the DOM so variant changes are reflected
      const ringImageUrl = this.currentRingImageUrl();
      const diamondImageUrl = this.dataset.diamondImageUrl;
      const ringSku = this.currentRingSku();
      const diamondCertNumber = this.dataset.diamondCertNumber;

      if (!ringImageUrl || !diamondImageUrl) {
        throw new Error("Ring image or diamond image not available");
      }

      // Fetch both images in parallel and convert to base64
      const [ringBase64, diamondBase64] = await Promise.all([this.imageToBase64(ringImageUrl), this.imageToBase64(diamondImageUrl)]);

      const body = {
        ringImage: { data: ringBase64.data, mimeType: ringBase64.mimeType },
        diamondImage: { data: diamondBase64.data, mimeType: diamondBase64.mimeType },
      };

      // Add optional identifiers for caching
      if (ringSku) body.ringSku = ringSku;
      if (diamondCertNumber) body.diamondCertNumber = diamondCertNumber;

      // Add diamond metadata
      const metaFields = ["shape", "carats", "color", "clarity", "cut", "fluorescence"];
      for (const field of metaFields) {
        if (this.dataset[field]) {
          body[field] = this.dataset[field];
        }
      }

      const apiUrl = window.diamonds_route || "https://mouza-nivoda-7d54f737c1b9.herokuapp.com/api";
      const response = await fetch(`${apiUrl}/images/swap-diamond-base64`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });

      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.error || `Server error: ${response.status}`);
      }

      let result = await response.json();
      if (!result.success && !result.processing) {
        throw new Error(result.error || "Failed to generate image");
      }

      // Async job pattern: server returns a jobId immediately, then processes
      // GenAI in the background to stay within Heroku's 30s request timeout.
      // We poll until the job completes.
      if (result.processing && result.jobId) {
        result = await this.pollJob(apiUrl, result.jobId);
      }

      // Get the image URL - either from Shopify cache or build from base64
      let imageUrl;
      if (result.result.shopifyUrl) {
        imageUrl = result.result.shopifyUrl;
      } else if (result.result.imageBase64) {
        const mime = result.result.mimeType || "image/png";
        imageUrl = `data:${mime};base64,${result.result.imageBase64}`;
      } else {
        throw new Error("No image in response");
      }

      this.insertIntoGallery(imageUrl);
      this.saveToSession(imageUrl);
      this.button.innerHTML = "Image Generated";
      resolveReady();
      await loaderPromise;
      this.removeInlineBar();
      // Hide the button container after successful generation
      setTimeout(() => {
        this.style.display = "none";
      }, 500);
    } catch (error) {
      console.error("Generate diamond image error:", error);
      resolveReady();
      await loaderPromise;
      this.removeInlineBar();
      this.button.style.display = '';
      this.showErrorState(error);
      this.generating = false;
    }
  }

  runColouredLoader(readyPromise) {
    const inline = this.ensureInlineBar();
    if (!inline) return Promise.resolve();
    const fill = inline.querySelector('[data-coloured-loader-fill]');
    const bar = inline.querySelector('.coloured-loader__bar');
    if (!fill) return Promise.resolve();

    fill.style.width = '0%';
    if (bar) bar.setAttribute('aria-valuenow', '0');

    // Asymptotic crawl 0 → 99% while waiting for the image (always moving),
    // then smooth → 100% once it lands. No fixed-duration phase.
    return this.crawlBar(fill, bar, 0, 99, 8000, readyPromise)
      .then((stoppedAt) => this.animateBar(fill, bar, stoppedAt, 100, 400));
  }

  removeInlineBar() {
    this.querySelector('[data-coloured-loader-inline]')?.remove();
  }

  // Inline progress bar rendered inside <generate-diamond-image>, replacing
  // the button visually for the duration of the generation.
  ensureInlineBar() {
    let inline = this.querySelector('[data-coloured-loader-inline]');
    if (inline) return inline;

    if (!document.getElementById('coloured-loader-style')) {
      const style = document.createElement('style');
      style.id = 'coloured-loader-style';
      style.textContent = `
        .coloured-loader-inline {
          width: 100%;
          padding: 14px 18px;
          background: #fff;
          border: 1px solid var(--light-khaki, #e6e1d6);
          text-align: center;
          box-sizing: border-box;
        }
        .coloured-loader__msg {
          font-family: 'Proxima Nova', sans-serif;
          font-weight: 600; font-size: 13px; letter-spacing: 0.04em;
          text-transform: uppercase; color: var(--dark-khaki, #8a7a4f);
          margin: 0 0 10px;
        }
        .coloured-loader__bar {
          position: relative; height: 2px; width: 100%;
          background-color: var(--light-khaki, #e6e1d6); overflow: hidden;
        }
        .coloured-loader__bar-fill {
          position: absolute; inset-block: 0; left: 0; width: 0%;
          background-color: var(--dark-khaki, #8a7a4f); will-change: width;
        }
      `;
      document.head.appendChild(style);
    }

    inline = document.createElement('div');
    inline.className = 'coloured-loader-inline';
    inline.setAttribute('data-coloured-loader-inline', '');
    inline.setAttribute('role', 'status');
    inline.setAttribute('aria-live', 'polite');
    inline.innerHTML = `
      <p class="coloured-loader__msg">Generating your coloured diamond ring</p>
      <div class="coloured-loader__bar" role="progressbar" aria-valuemin="0" aria-valuemax="100" aria-valuenow="0">
        <span class="coloured-loader__bar-fill" data-coloured-loader-fill></span>
      </div>
    `;
    this.appendChild(inline);
    return inline;
  }

  animateBar(fill, bar, fromPct, toPct, duration) {
    return new Promise((resolve) => {
      const start = performance.now();
      const span = toPct - fromPct;
      const tick = (now) => {
        const progress = Math.min((now - start) / duration, 1);
        const pct = fromPct + span * progress;
        fill.style.width = pct.toFixed(3) + '%';
        if (bar) bar.setAttribute('aria-valuenow', String(Math.round(pct)));
        progress < 1 ? requestAnimationFrame(tick) : resolve();
      };
      requestAnimationFrame(tick);
    });
  }

  // Asymptotic approach toward `targetPct` — bar visibly creeps forward the
  // whole time it's waiting, but never reaches 100% until the real signal lands.
  crawlBar(fill, bar, fromPct, targetPct, tau, stopSignal) {
    return new Promise((resolve) => {
      let stopped = false;
      stopSignal.then(() => { stopped = true; });

      const start = performance.now();
      let lastPct = fromPct;
      const tick = (now) => {
        if (stopped) return resolve(lastPct);
        const elapsed = now - start;
        lastPct = targetPct - (targetPct - fromPct) * Math.exp(-elapsed / tau);
        fill.style.width = lastPct.toFixed(3) + '%';
        if (bar) bar.setAttribute('aria-valuenow', String(Math.round(lastPct)));
        requestAnimationFrame(tick);
      };
      requestAnimationFrame(tick);
    });
  }

  showErrorState(error) {
    const message = this.friendlyErrorMessage(error);
    this.button.innerHTML = `${message} — Try again`;
    this.button.disabled = false;
    this.button.classList.add("generate-image-error");
    if (!document.getElementById("generate-image-error-style")) {
      const style = document.createElement("style");
      style.id = "generate-image-error-style";
      style.textContent = `
        .generate-image-error {
          color: #b00020;
          border-color: #b00020 !important;
        }
      `;
      document.head.appendChild(style);
    }
    const reset = () => {
      this.button.classList.remove("generate-image-error");
      this.button.innerHTML = this.originalButtonHTML;
      this.button.removeEventListener("click", reset, { capture: true });
    };
    this.button.addEventListener("click", reset, { capture: true, once: true });
  }

  friendlyErrorMessage(error) {
    const raw = typeof error?.message === "string" ? error.message : String(error || "");
    try {
      const parsed = JSON.parse(raw);
      const code = parsed?.error?.code ?? parsed?.error?.status;
      if (code === 503 || code === "UNAVAILABLE") return "Service is busy";
      if (code === 429 || code === "RESOURCE_EXHAUSTED") return "Too many requests";
    } catch {
      // not JSON — fall through
    }
    if (/timed out/i.test(raw)) return "Generation timed out";
    return "Generation failed";
  }

  async imageToBase64(url) {
    const response = await fetch(url);
    const blob = await response.blob();
    const mimeType = blob.type || "image/jpeg";

    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64 = reader.result.split(",")[1];
        resolve({ data: base64, mimeType });
      };
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  }

  // Poll the async job endpoint until GenAI processing completes.
  // The server returns a jobId immediately to avoid Heroku's 30s timeout;
  // we check every 3s for up to ~2 minutes.
  async pollJob(apiUrl, jobId) {
    const POLL_INTERVAL = 3000;
    const MAX_ATTEMPTS = 40; // ~2 minutes

    for (let i = 0; i < MAX_ATTEMPTS; i++) {
      await new Promise((r) => setTimeout(r, POLL_INTERVAL));

      const res = await fetch(`${apiUrl}/images/jobs/${jobId}`);
      const data = await res.json();

      if (data.status === "completed") return data;
      if (data.status === "failed") throw new Error(data.error || "Image generation failed");
      // still processing — continue polling
    }

    throw new Error("Image generation timed out");
  }

  insertIntoGallery(imageUrl) {
    const slidesContainer = document.querySelector(".MagicToolboxSlides");
    const selectorsContainer = document.querySelector(".MagicToolboxSelectorsContainer");

    if (!slidesContainer) {
      console.warn("MagicToolboxSlides container not found");
      return;
    }

    const slideId = "ai-generated-diamond";

    // Remove existing AI slide if present
    const existingSlide = slidesContainer.querySelector(`[data-slide-id="${slideId}"]`);
    if (existingSlide) existingSlide.remove();
    const existingThumb = selectorsContainer?.querySelector(`[data-slide-id="${slideId}"]`);
    if (existingThumb) existingThumb.remove();

    // Deactivate current active slide (skip the spin/360 slide — Sirv manages its own state)
    slidesContainer.querySelectorAll(".active-magic-slide").forEach((el) => {
      if (el.dataset.slideId !== "spin") el.classList.remove("active-magic-slide");
    });
    selectorsContainer?.querySelectorAll(".active-magic-selector").forEach((el) => el.classList.remove("active-magic-selector"));

    // Create new slide
    const slide = document.createElement("div");
    slide.className = "MagicToolboxSlide desktop-slide active-magic-slide";
    slide.dataset.slideId = slideId;
    slide.innerHTML = `
      <span class="ai-generated-label">Generated image</span>
      <a id="ai-zoom-link" href="${imageUrl}" class="js-no-transition MagicZoomPlus" data-gallery="zoom" title="AI Generated Diamond Image" data-image="${imageUrl}">
        <img src="${imageUrl}" alt="AI Generated Diamond Image" style="width:100%;aspect-ratio:1/1;object-fit:contain;" />
      </a>
    `;
    if (!document.getElementById('ai-generated-label-style')) {
      const style = document.createElement('style');
      style.id = 'ai-generated-label-style';
      style.textContent = `
        [data-slide-id="ai-generated-diamond"] {
          position: relative;
        }
        .ai-generated-label {
          position: absolute;
          top: 10px;
          left: 10px;
          z-index: 10;
          color: #333;
          font-size: 12px;
          pointer-events: none;
          letter-spacing: 0.02em;
        }
      `;
      document.head.appendChild(style);
    }

    // Insert after the main zoom slide (first product image), keeping spin/360 untouched
    const zoomSlide = slidesContainer.querySelector('[data-slide-id="zoom"]');
    if (zoomSlide) {
      zoomSlide.after(slide);
    } else {
      // Fallback: insert as first image slide, after any nav arrows
      const firstSlide = slidesContainer.querySelector(".MagicToolboxSlide");
      if (firstSlide) {
        firstSlide.after(slide);
      } else {
        slidesContainer.append(slide);
      }
    }

    // Create thumbnail
    if (selectorsContainer) {
      const scrollContainer = selectorsContainer.querySelector(".MagicScroll") || selectorsContainer;
      const thumb = document.createElement("a");
      thumb.className = "js-no-transition active-magic-selector";
      thumb.dataset.slideId = slideId;
      thumb.href = "#";
      thumb.innerHTML = `<img src="${imageUrl}" alt="AI Generated" style="width:80px;height:80px;object-fit:contain;" />`;

      // Add click handler to the new thumb so it switches back to this slide
      thumb.addEventListener('click', (e) => {
        e.preventDefault();

        // Update thumbnail active state
        selectorsContainer.querySelectorAll('.active-magic-selector').forEach(el => el.classList.remove('active-magic-selector'));
        thumb.classList.add('active-magic-selector');

        // Update slide active state
        slidesContainer.querySelectorAll('.active-magic-slide').forEach(el => el.classList.remove('active-magic-slide'));
        slide.classList.add('active-magic-slide');
      });

      // Handle insertion properly for MagicScroll
      const isScrollActive = typeof MagicScroll !== 'undefined' && scrollContainer.classList.contains('MagicScroll');
      if (isScrollActive) {
        try {
          MagicScroll.stop();
        } catch (e) {
          console.error("MagicScroll stop error", e);
        }
      }

      scrollContainer.prepend(thumb);

      if (isScrollActive) {
        try {
          MagicScroll.start();
        } catch (e) {
          console.error("MagicScroll start error", e);
        }
      }
    }

    // Initialize Magic Zoom strictly for the new slide to avoid breaking existing Sirv/Spin slides
    if (typeof MagicZoom !== 'undefined' && typeof MagicZoom.start === 'function') {
      try {
        if (document.getElementById('ai-zoom-link')) {
          MagicZoom.start('ai-zoom-link');
        }
      } catch (e) {
        console.error("MagicZoom start error", e);
      }
    }
  }
}

customElements.define("generate-diamond-image", GenerateDiamondImage);
